import { RefObject, useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { scaleDisplayEnabledState } from '~/state/atoms/toolsAtoms';
export interface ScaleDisplayProps {
    view: PlaneView | null;
    canvasRef: RefObject<HTMLCanvasElement | null>;
}

export interface ScaleDisplayState {
    barPixelWidth: number;
    subdivisionCount: number;
    totalDistanceMm: number;
}

// minimum fraction of available width to use for the scale bar (6%)
const MIN_SCALE_WIDTH_RATIO = 0.06;
// maximum fraction of available width to use for the scale bar (25%)
const MAX_SCALE_WIDTH_RATIO = 0.25;

// preferred physical lengths (in millimeters) for the scale bar.
// these values are chosen to provide standard, visually meaningful increments for medical imaging rulers,
// ensuring the scale bar is neither too large nor too small for typical viewport sizes.
const PREFERRED_LENGTHS_MM: number[] = [150, 100, 50, 25, 10, 5];

const selectPreferredLength = (mmPerPx: number, availableWidthPx: number) => {
    const minPx = Math.max(
        40,
        Math.round(availableWidthPx * MIN_SCALE_WIDTH_RATIO)
    );
    const maxPx = Math.max(
        120,
        Math.round(availableWidthPx * MAX_SCALE_WIDTH_RATIO)
    );
    for (const lengthMm of PREFERRED_LENGTHS_MM) {
        const px = lengthMm / mmPerPx;
        if (px >= minPx && px <= maxPx) {
            return { mm: lengthMm, px };
        }
    }
    const mid = (minPx + maxPx) / 2;
    let best = {
        mm: PREFERRED_LENGTHS_MM[0],
        px: PREFERRED_LENGTHS_MM[0] / mmPerPx,
    };
    let bestDiff = Math.abs(best.px - mid);
    for (const lengthMm of PREFERRED_LENGTHS_MM) {
        const px = lengthMm / mmPerPx;
        const diff = Math.abs(px - mid);
        if (diff < bestDiff) {
            best = { mm: lengthMm, px };
            bestDiff = diff;
        }
    }
    return best;
};

/**
 * Hook to manage scale display parameters based on the current view and canvas size.
 * - Calculates appropriate scale bar width, subdivision count, and total distance.
 * - Reacts to zoom scale changes in the view manager.
 *
 * Note: returns distance in mm (domain unit). The UI component is responsible
 * for converting to cm for display (application layer convention).
 */
export function useScaleDisplayHandler({
    view,
    canvasRef,
}: ScaleDisplayProps): ScaleDisplayState {
    const viewManager = useRecoilValue(viewManagerState);
    const scaleDisplayEnabled = useRecoilValue(scaleDisplayEnabledState);

    const [barPixelWidth, setBarPixelWidth] = useState<number>(0);
    const [subdivisionCount, setSubdivisionCount] = useState<number>(10);
    const [totalDistanceMm, setTotalDistanceMm] = useState<number>(0);

    const measureAndUpdateScaleDisplay = useCallback(() => {
        const resolution = view?.resolution;
        if (!resolution) {
            return;
        }

        const canvasWidth = canvasRef.current?.getBoundingClientRect().width;
        if (!canvasWidth) {
            return;
        }

        const mmPerPixel = resolution[0];
        const { mm, px } = selectPreferredLength(mmPerPixel, canvasWidth);

        const subdivisions = Math.min(
            10,
            Math.max(1, Math.round(mm >= 100 ? 10 : mm))
        );

        setBarPixelWidth(px);
        setSubdivisionCount(subdivisions);
        setTotalDistanceMm(mm);
    }, [view, canvasRef]);

    useEffect(() => {
        if (!viewManager || !scaleDisplayEnabled) {
            return;
        }
        viewManager.addEventListener(
            'zoomScaleChanged',
            measureAndUpdateScaleDisplay
        );
        return () => {
            viewManager.removeEventListener(
                'zoomScaleChanged',
                measureAndUpdateScaleDisplay
            );
        };
    }, [viewManager, scaleDisplayEnabled, measureAndUpdateScaleDisplay]);

    useEffect(() => {
        if (scaleDisplayEnabled) {
            measureAndUpdateScaleDisplay();
        }
    }, [scaleDisplayEnabled, measureAndUpdateScaleDisplay]);

    return { barPixelWidth, subdivisionCount, totalDistanceMm };
}
